import { useContentTags } from "@gik/api/blog/contentTags";
import { useTaxonomies } from "@gik/api/give/taxonomies";
import type Category from "@gik/blog/models/Category";
import WordpressConverter from "@gik/blog/utils/WordpressConverter";
import { DynamicComponent } from "@gik/core/components/DynamicComponent";
import Link from "@gik/core/components/Link.tsx";
import type WordpressSituation from "@gik/core/models/wordpress/WordpressSituation";
import type WordpressTag from "@gik/core/models/wordpress/WordpressTag";
import { useBemCN } from "@gik/core/utils/bemBlock";
import getPreviewMode, {
	PreviewModeType,
	turnPreviewModeOff,
} from "@gik/core/utils/PreviewMode";
import { Button } from "@gik/ui-legacy/Button";
import { MenuBar } from "@gik/ui-legacy/Menu";
import { NavBar } from "@gik/ui-legacy/NavBar";
import ChevronDown from "@heroicons/react/solid/ChevronDownIcon";
import React from "react";

export interface IBlogNavProps {
	situations: WordpressSituation[];
	tags: Category[];
}

const blockName = "blog-nav";

function BlogNavComp({ situations, tags }: IBlogNavProps) {
	const bem = useBemCN(blockName);
	const blogPreviewMode = getPreviewMode(PreviewModeType.Blog);

	const convertedSituations = React.useMemo(
		() =>
			situations?.map((situation) => ({
				label: situation.name,
				href: `/articles/general-results/situation/${situation.slug}`,
				category: situations.find(
					(_situation) => _situation.id === situation.parent,
				)?.name,
			})) || [],
		[situations],
	);

	const convertedPopularTags = React.useMemo(
		() =>
			tags
				?.filter((tag) => tag.postCount > 0)
				.sort((a, b) => b.postCount - a.postCount)
				.slice(0, 10)
				.map((tag) => ({
					label: tag.name,
					href: `/articles/general-results/content_tag/${tag.slug}`,
					category: "Popular tags",
					hideOnSearch: true,
				})) || [],
		[tags],
	);

	const convertedAllTags = React.useMemo(
		() =>
			tags
				?.filter((tag) => tag.postCount > 0)
				.map((tag) => ({
					label: tag.name,
					href: `/articles/general-results/content_tag/${tag.slug}`,
					category: "All Tags",
				})) || [],
		[tags],
	);

	const menuBarList = React.useMemo(() => {
		return [
			{
				title: "Situations",
				appendIcon: ChevronDown,
				menu: convertedSituations,
				categorize: true,
				sortCategories: true,
			},
			{
				title: "Spotlight",
				href: `/articles/general-results/article-type/spotlight`,
			},
			{
				title: "Being Supportive",
				href: `/articles/general-results/article-type/being-supportive`,
			},
			{
				title: "Recipes",
				href: `/articles/general-results/article-type/recipe`,
			},
			{
				title: "Tags",
				appendIcon: ChevronDown,
				menu: [...convertedPopularTags, ...convertedAllTags],
				search: true,
				categorize: true,
				sortCategories: false,
			},
		];
	}, [convertedAllTags, convertedPopularTags, convertedSituations]);

	const menuBarListMobile = React.useMemo(() => {
		return [
			{
				title: "Jump to",
				appendIcon: ChevronDown,
				menu: [
					{
						label: "Situations",
						items: convertedSituations,
						categorize: true,
						sortCategories: true,
					},
					{
						label: "Spotlight",
						href: `/articles/general-results/article-type/spotlight`,
					},
					{
						label: "Being Supportive",
						href: `/articles/general-results/article-type/being-supportive`,
					},
					{
						label: "Recipes",
						href: `/articles/general-results/article-type/recipe`,
					},
					{
						label: "Tags",
						items: [...convertedPopularTags, ...convertedAllTags],
						search: true,
						categorize: true,
						sortCategories: false,
					},
				],
			},
		];
	}, [convertedAllTags, convertedPopularTags, convertedSituations]);

	return (
		<NavBar
			contained
			{...bem(null, [{ "preview-mode": blogPreviewMode }])}
			containerClassName={bem("container").className}
		>
			<Link href="/articles">
				<h1 {...bem("title")}>Learn InKind</h1>
				{blogPreviewMode && (
					<div {...bem("preview-mode-container")}>
						PREVIEW MODE{" "}
						<Button
							size={"xs"}
							variant={"danger"}
							onClick={() => turnPreviewModeOff(PreviewModeType.Blog)}
						>
							Turn off
						</Button>
					</div>
				)}
			</Link>
			<MenuBar
				{...bem("menu")}
				menuList={menuBarList}
				hasArrowIndicator
				placement={"bottom-start"}
			/>
			<MenuBar
				{...bem("menu", [{ mobile: true }])}
				menuList={menuBarListMobile}
				hasArrowIndicator
				placement={"bottom-start"}
			/>
		</NavBar>
	);
}

export function BlogNav({
	...otherProps
}: Omit<React.ComponentProps<typeof BlogNavComp>, "tags" | "situations">) {
	const bem = useBemCN(blockName);
	const loadingComponent = (
		<NavBar contained>
			<Link href="/articles">
				<h1 {...bem("title")}>Learn InKind</h1>
			</Link>
		</NavBar>
	);

	return (
		<DynamicComponent<WordpressSituation[]>
			height={90}
			fetch={function useFetch() {
				return useTaxonomies("situation");
			}}
			loadingComponent={() => loadingComponent}
		>
			{({ data: situations }) => {
				return (
					<DynamicComponent<WordpressTag[], Category[]>
						height={90}
						fetch={function useFetch() {
							return useContentTags({
								_fields: BlogNav.requiredWPFields.tags,
							});
						}}
						transform={(tags) => tags.map(WordpressConverter.convertTag)}
						loadingComponent={() => loadingComponent}
					>
						{({ transformed: tags }) => {
							return (
								<BlogNavComp
									tags={tags}
									situations={situations}
									{...otherProps}
								/>
							);
						}}
					</DynamicComponent>
				);
			}}
		</DynamicComponent>
	);
}

BlogNav.requiredWPFields = {
	situations: ["name", "slug", "id", "parent"],
	tags: ["count-post-content_tag", "name", "slug"],
};
